import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import { db } from "./firebase";
import { collection, addDoc, getDocs } from "firebase/firestore";

const CATEGORY_ORDER = ["CBU", "RPP", "SMB", "FIDO", "Other"];
const PINNED_REP_ORDER = ["AA8", "DN", "MR5", "YK"];
const OTHER_REP_LABEL = "Other";

const PRODUCT_ORDER_BY_CATEGORY = {
  CBU: [
    "CBU VOICE",
    "CBU TERM",
    "CBU HUP",
    "CBU TVM HUP",
    "CBU HUP RD",
    "CBU TVM HUP RD",
    "CBU 5GHI",
    "CBU MBB",
  ],
  RPP: [
    "RPP VOICE",
    "RPP TERM",
    "RPP HUP",
    "RPP TVM HUP",
    "RPP HUP RD",
    "RPP TVM HUP RD",
    "RPP 5GHI",
    "RPP MBB",
  ],
  SMB: [
    "SMB VOICE",
    "SMB TERM",
    "SMB HUP",
    "SMB TVM HUP",
    "SMB HUP RD",
    "SMB TVM HUP RD",
    "SMB 5GHI",
    "SMB MBB",
    "SMB CABLE",
  ],
  FIDO: [
    "FIDO VOICE",
    "FIDO TERM",
    "FIDO HUP",
    "FIDO TVM HUP",
    "FIDO HUP RD",
    "FIDO TVM HUP RD",
    "FIDO MBB",
    "FIDO EXPU_N",
    "FIDO EXPU_H",
    "FIDO EXPU_V",
  ],
  Other: [
    "DP",
    "ACC",
    "EXPU_N",
    "EXPU_H",
    "EXPU_V",
    "MC APPROVED",
    "MC UNDERREVIEW",
    "CABLE",
    "CHATR",
    "OUTRIGHT SALE",
    "COMWAVE",
  ],
};

const getOrderedIndex = (value, orderedList) => {
  const index = orderedList.indexOf(value);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const sortRepsForStore = (repNames) => {
  const cleaned = repNames.filter(Boolean);

  return [...cleaned].sort((a, b) => {
    const aIsOther = a.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();
    const bIsOther = b.trim().toLowerCase() === OTHER_REP_LABEL.toLowerCase();

    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;

    const aPinnedIndex = PINNED_REP_ORDER.indexOf(a);
    const bPinnedIndex = PINNED_REP_ORDER.indexOf(b);

    const aIsPinned = aPinnedIndex !== -1;
    const bIsPinned = bPinnedIndex !== -1;

    if (aIsPinned && bIsPinned) return aPinnedIndex - bPinnedIndex;
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    return a.localeCompare(b);
  });
};

export default function EODLogger({
  selectedProfile,
  lockedRepCode,
  isStoreMode,
}) {
  const [reps, setReps] = useState([]);
  const [products, setProducts] = useState([]);
  const [repData, setRepData] = useState(() =>
    isStoreMode
      ? [{ rep: "", customers: [] }]
      : [{ rep: lockedRepCode || "", customers: [] }]
  );
  const [showDisplay, setShowDisplay] = useState(false);
  const [displayText, setDisplayText] = useState("");
  const [popup, setPopup] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [repSnapshot, productSnapshot] = await Promise.all([
          getDocs(collection(db, "reps")),
          getDocs(collection(db, "products")),
        ]);

        const repList = repSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .map((rep) => rep.name)
          .filter(Boolean);

        const productList = productSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((product) => product.active)
          .sort((a, b) => {
            const categoryA = a.category || "Other";
            const categoryB = b.category || "Other";

            const categoryDiff =
              getOrderedIndex(categoryA, CATEGORY_ORDER) -
              getOrderedIndex(categoryB, CATEGORY_ORDER);

            if (categoryDiff !== 0) return categoryDiff;

            const orderedProducts = PRODUCT_ORDER_BY_CATEGORY[categoryA] || [];
            const productDiff =
              getOrderedIndex(a.name || "", orderedProducts) -
              getOrderedIndex(b.name || "", orderedProducts);

            if (productDiff !== 0) return productDiff;

            return (a.name || "").localeCompare(b.name || "");
          });

        setReps(sortRepsForStore(repList));
        setProducts(productList);
      } catch (error) {
        console.error("Failed to load data:", error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    setRepData(
      isStoreMode
        ? [{ rep: "", customers: [] }]
        : [{ rep: lockedRepCode || "", customers: [] }]
    );
    setShowDisplay(false);
    setDisplayText("");
  }, [isStoreMode, lockedRepCode, selectedProfile]);

  const productsByType = useMemo(() => {
    const grouped = {};

    products.forEach((product) => {
      const category = product.category || "Other";
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(product.name);
    });

    Object.keys(grouped).forEach((category) => {
      const orderedProducts = PRODUCT_ORDER_BY_CATEGORY[category] || [];

      grouped[category] = grouped[category].sort((a, b) => {
        const orderDiff =
          getOrderedIndex(a, orderedProducts) -
          getOrderedIndex(b, orderedProducts);

        if (orderDiff !== 0) return orderDiff;

        return a.localeCompare(b);
      });
    });

    return grouped;
  }, [products]);

  const typeKeys = useMemo(() => {
    return Object.keys(productsByType).sort((a, b) => {
      const orderDiff =
        getOrderedIndex(a, CATEGORY_ORDER) -
        getOrderedIndex(b, CATEGORY_ORDER);

      if (orderDiff !== 0) return orderDiff;

      return a.localeCompare(b);
    });
  }, [productsByType]);

  const isDPEligible = (name) =>
    name.includes("HUP") ||
    name.includes("TERM") ||
    name === "EXPU_H" ||
    name === "EXPU_N";

  const showPopup = (message, type = "success") => {
    setPopup({ show: true, message, type });
    setTimeout(() => {
      setPopup({ show: false, message: "", type: "success" });
    }, 2200);
  };

  const addRep = () => {
    if (!isStoreMode) return;
    setRepData([...repData, { rep: "", customers: [] }]);
  };

  const addCustomer = (repIndex) => {
    const newData = [...repData];
    newData[repIndex].customers.push({
      name: "",
      selectedTypes: [],
      openTypes: [],
      products: [],
      hadMCBefore: false,
      aalCount: 0,
    });
    setRepData(newData);
  };

  const handleRepChange = (index, value) => {
    if (!isStoreMode) return;
    const newData = [...repData];
    newData[index] = { rep: value, customers: [] };
    setRepData(newData);
  };

  const handleCustomerName = (repIndex, custIndex, value) => {
    const newData = [...repData];
    newData[repIndex].customers[custIndex].name = value;
    setRepData(newData);
  };

  const toggleFlag = (repIndex, custIndex, field) => {
    const newData = [...repData];
    newData[repIndex].customers[custIndex][field] =
      !newData[repIndex].customers[custIndex][field];
    setRepData(newData);
  };

  const changeAALCount = (repIndex, custIndex, change) => {
    const newData = [...repData];
    const customer = newData[repIndex].customers[custIndex];
    customer.aalCount = Math.max(0, (customer.aalCount || 0) + change);
    setRepData(newData);
  };

  const handleTypeClick = (repIndex, custIndex, type) => {
    const newData = [...repData];
    const cust = newData[repIndex].customers[custIndex];
    const categoryProducts = productsByType[type] || [];

    if (!cust.selectedTypes) cust.selectedTypes = [];
    if (!cust.openTypes) cust.openTypes = [];

    const isSelected = cust.selectedTypes.includes(type);
    const isOpen = cust.openTypes.includes(type);

    if (!isSelected) {
      cust.selectedTypes.push(type);
      cust.openTypes.push(type);

      categoryProducts.forEach((product) => {
        if (!cust.products.find((p) => p.name === product)) {
          cust.products.push({ name: product, qty: 0, dp: [] });
        }
      });

      setRepData(newData);
      return;
    }

    if (isOpen) {
      cust.openTypes = cust.openTypes.filter((t) => t !== type);
    } else {
      cust.openTypes.push(type);
    }

    setRepData(newData);
  };

  const changeQty = (repIndex, custIndex, product, change) => {
    const newData = [...repData];
    const customer = newData[repIndex].customers[custIndex];

    customer.products = customer.products.map((p) => {
      if (p.name !== product) return p;

      const newQty = Math.max(0, (p.qty || 0) + change);
      let dpArray = p.dp || [];

      if (isDPEligible(p.name)) {
        if (change > 0) dpArray = [...dpArray, { value: "NONE" }];
        if (change < 0) dpArray = dpArray.slice(0, -1);
      }

      return { ...p, qty: newQty, dp: dpArray };
    });

    setRepData(newData);
  };

  const setDPValue = (repIndex, custIndex, productName, index, value) => {
    const newData = [...repData];

    const product = newData[repIndex].customers[custIndex].products.find(
      (p) => p.name === productName
    );

    if (!product?.dp || !product.dp[index]) return;

    product.dp[index] = { value };
    setRepData(newData);
  };

  const buildSummary = () => {
    let summary = "";

    repData.forEach((rep) => {
      if (!rep.rep) return;

      const totals = {};
      let dpCount = 0;
      let aalCount = 0;

      rep.customers.forEach((cust) => {
        aalCount += cust.aalCount || 0;

        cust.products.forEach((p) => {
          if (p.qty > 0) {
            totals[p.name] = (totals[p.name] || 0) + p.qty;
          }

          (p.dp || []).forEach((entry) => {
            if (entry && entry.value === "YES") {
              dpCount++;
            }
          });
        });
      });

      const entries = Object.entries(totals);

      if (entries.length || dpCount || aalCount) {
        summary += `${rep.rep}: `;
        const text = entries.map(([n, q]) => `${q} ${n}`);
        if (dpCount) text.push(`${dpCount} DP`);
        if (aalCount) text.push(`${aalCount} AAL`);
        summary += text.join(", ") + "\n\n";
      }
    });

    return summary.trim();
  };

  const displaySummary = () => {
    const summary = buildSummary();
    const today = new Date().toLocaleDateString("en-CA");
    const finalText = summary ? `Date: ${today}\n\n${summary}` : `Date: ${today}`;
    setDisplayText(finalText);
    setShowDisplay(true);
  };

  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const sanitizeCustomerForSave = (cust) => ({
    name: cust.name || "",
    selectedTypes: Array.isArray(cust.selectedTypes) ? cust.selectedTypes : [],
    products: Array.isArray(cust.products)
      ? cust.products.map((p) => ({
          name: p.name || "",
          qty: Number(p.qty || 0),
          dp: Array.isArray(p.dp)
            ? p.dp.map((entry) => ({
                value: entry?.value || "NONE",
              }))
            : [],
        }))
      : [],
    hadMCBefore: !!cust.hadMCBefore,
    aalCount: Number(cust.aalCount || 0),
  });

  const saveData = async () => {
    const now = new Date();
    const date = getLocalDateString();
    const time = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

    const repsToSave = repData.filter((rep) => rep.rep && rep.rep.trim());

    if (repsToSave.length === 0) {
      throw new Error("No rep selected");
    }

    for (let rep of repsToSave) {
      await addDoc(collection(db, "eodReports"), {
        date,
        time,
        rep: rep.rep.trim(),
        customers: Array.isArray(rep.customers)
          ? rep.customers.map(sanitizeCustomerForSave)
          : [],
      });
    }
  };

  const handleSave = async () => {
    try {
      await saveData();
      showPopup("Report saved successfully ✅", "success");
    } catch (error) {
      console.error(error);
      showPopup("Failed to save report ❌", "error");
    }
  };

  const sendWhatsApp = async () => {
    const summary = buildSummary();

    if (!summary) {
      showPopup("Nothing to send 😭", "error");
      return;
    }

    try {
      await saveData();
      const encoded = encodeURIComponent(summary);
      window.location.href = `https://api.whatsapp.com/send?text=${encoded}`;
    } catch (error) {
      console.error(error);
      showPopup("Failed to save before WhatsApp ❌", "error");
    }
  };

  return (
    <div className="app-shell">
      {popup.show && (
        <div className={`save-popup ${popup.type}`}>
          {popup.message}
        </div>
      )}

      {!showDisplay &&
        repData.map((rep, repIndex) => (
          <div key={repIndex} className="rep-card">
            {isStoreMode ? (
              <select
                className="rep-select"
                value={rep.rep}
                onChange={(e) => handleRepChange(repIndex, e.target.value)}
              >
                <option value="">Select Rep</option>
                {reps.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            ) : (
              <div className="rep-display">
                Rep: {rep.rep || lockedRepCode}
              </div>
            )}

            <button
              className="add-customer-btn"
              onClick={() => addCustomer(repIndex)}
            >
              + Add Customer
            </button>

            {rep.customers.map((cust, custIndex) => (
              <div key={custIndex} className="customer-card">
                <input
                  placeholder="Customer Name (optional)"
                  className="customer-input"
                  value={cust.name}
                  onChange={(e) =>
                    handleCustomerName(repIndex, custIndex, e.target.value)
                  }
                />

                <div className="customer-options">
                  <div className="flags-row">
                    <label className="check-label">
                      <input
                        type="checkbox"
                        checked={cust.hadMCBefore}
                        onChange={() => toggleFlag(repIndex, custIndex, "hadMCBefore")}
                      />
                      Existing MC
                    </label>

                    <div className="aal-counter-wrap">
                      <span className="aal-label">AAL</span>
                      <div className="aal-counter">
                        <button
                          type="button"
                          className="qty-btn qty-btn-outline"
                          onClick={() => changeAALCount(repIndex, custIndex, -1)}
                        >
                          -
                        </button>

                        <span className="qty-value">{cust.aalCount || 0}</span>

                        <button
                          type="button"
                          className="qty-btn qty-btn-filled"
                          onClick={() => changeAALCount(repIndex, custIndex, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="type-buttons">
                    {typeKeys.map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`type-btn ${
                          cust.openTypes?.includes(type) ? "active" : ""
                        }`}
                        onClick={() => handleTypeClick(repIndex, custIndex, type)}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {(cust.selectedTypes || []).map((type) => {
                  const isOpen = cust.openTypes?.includes(type);
                  if (!isOpen) return null;

                  return (
                    <div key={type} className="type-section">
                      <h4 className="type-title">{type}</h4>

                      {cust.products
                        .filter((p) => (productsByType[type] || []).includes(p.name))
                        .sort((a, b) => {
                          const orderedProducts = PRODUCT_ORDER_BY_CATEGORY[type] || [];
                          const orderDiff =
                            getOrderedIndex(a.name, orderedProducts) -
                            getOrderedIndex(b.name, orderedProducts);

                          if (orderDiff !== 0) return orderDiff;

                          return a.name.localeCompare(b.name);
                        })
                        .map((p) => (
                          <div key={p.name} className="product-row-card">
                            <div className="product-name">{p.name}</div>

                            <div className="qty-controls">
                              <button
                                type="button"
                                className="qty-btn qty-btn-outline"
                                onClick={() =>
                                  changeQty(repIndex, custIndex, p.name, -1)
                                }
                              >
                                -
                              </button>

                              <span className="qty-value">{p.qty}</span>

                              <button
                                type="button"
                                className="qty-btn qty-btn-filled"
                                onClick={() =>
                                  changeQty(repIndex, custIndex, p.name, 1)
                                }
                              >
                                +
                              </button>
                            </div>

                            {isDPEligible(p.name) && p.dp.length > 0 && (
                              <div className="dp-row">
                                {p.dp.map((entry, i) => (
                                  <div key={i} className="dp-select-wrap">
                                    <span className="dp-label-text">DP {i + 1}</span>
                                    <select
                                      className="dp-select"
                                      value={entry?.value || "NONE"}
                                      onChange={(e) =>
                                        setDPValue(
                                          repIndex,
                                          custIndex,
                                          p.name,
                                          i,
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="NONE">No</option>
                                      <option value="YES">Yes</option>
                                      <option value="HAS">Existing</option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        ))}

      {!showDisplay && (
        <>
          {isStoreMode && (
            <button className="secondary-btn add-rep-btn" onClick={addRep}>
              + Add Rep
            </button>
          )}

          <button className="display-btn" onClick={displaySummary}>
            Display
          </button>
        </>
      )}

      {showDisplay && (
        <div className="summary-card">
          <textarea
            value={displayText}
            readOnly
            className="summary-textarea"
          />

          <div className="summary-actions">
            <button className="secondary-btn" onClick={() => setShowDisplay(false)}>
              Back
            </button>

            <button className="secondary-btn" onClick={handleSave}>
              Save
            </button>

            <button className="whatsapp-btn" onClick={sendWhatsApp}>
              WhatsApp
            </button>
          </div>
        </div>
      )}
    </div>
  );
}